import React, { Component } from 'react';
import PropTypes from 'prop-types';

import { Spinner } from 'reactstrap';

import moment from 'moment';

// Shared pieces of the Website Connection screens (Shopify, WooCommerce, Manual): numbered step cards,
// copy fields, the "please don't" box, the test result list and the one-line Connected summary.
// Colours from the approved design (4 Nov 2026); buttons use the darker blue so white text is readable.

export const colors = {
  blue: '#1F6FC5',
  blueDark: '#174F8C',
  blueLight: '#CFE6FD',
  page: '#F3F8FD',
  text: '#24323F',
  muted: '#4F5F6F',
  border: '#C9D6E2',
  borderLight: '#E3EBF2',
  green: '#1B6B3C',
  greenLight: '#E4F4EA',
  red: '#A12B2B',
  amberBg: '#FFF4E0',
  amberText: '#6E4200',
  amberIcon: '#8A5300',
};

export const cardStyle = {
  background: '#FFFFFF',
  borderRadius: 16,
  padding: '24px clamp(16px, 3vw, 32px)',
  boxShadow: '0 1px 3px rgba(20,40,60,.08)',
  marginBottom: 20,
  color: colors.text,
};

export const headingStyle = { margin: 0, fontSize: 20, fontWeight: 600, color: colors.text };

export const smallCapsStyle = {
  margin: '8px 0 10px', fontSize: 13, fontWeight: 600, letterSpacing: '.08em', textTransform: 'uppercase', color: colors.muted,
};

export const inputStyle = {
  fontSize: 16, padding: '12px 14px', height: 'auto', border: `1px solid ${colors.border}`, borderRadius: 10, width: '100%',
};

export const labelStyle = { fontSize: 14, color: colors.muted, marginBottom: 6 };

// '' -> '', 'abcdef1234' -> four bullets + '1234'
export const maskSecret = (secret) => {
  const s = String(secret || '');
  if (!s) return '';
  return `\u2022\u2022\u2022\u2022${s.slice(-4)}`;
};

export const lastChecked = (time) => {
  if (!time) return null;
  const m = moment(time);
  return m.isValid() ? m.fromNow() : null;
};

const CheckIcon = ({ color, size }) => (
  <svg aria-hidden="true" width={size || 18} height={size || 18} viewBox="0 0 24 24" fill="none" stroke={color || colors.green} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ flex: 'none', marginTop: 3 }}>
    <path d="M5 12l5 5L20 7" />
  </svg>
);

const CrossIcon = () => (
  <svg aria-hidden="true" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={colors.red} strokeWidth="2.5" strokeLinecap="round" style={{ flex: 'none', marginTop: 3 }}>
    <path d="M6 6l12 12" />
    <path d="M18 6L6 18" />
  </svg>
);

export const PrimaryButton = ({ children, onClick, disabled, busy, type }) => (
  <button
    type={type || 'button'}
    onClick={onClick}
    disabled={disabled || busy}
    style={{
      fontSize: 16, fontWeight: 600, border: 0, borderRadius: 999, padding: '12px 32px', background: colors.blue, color: '#FFFFFF',
      opacity: (disabled || busy) ? 0.7 : 1, cursor: (disabled || busy) ? 'default' : 'pointer', display: 'inline-flex', alignItems: 'center', gap: 10,
    }}
  >
    {children}
    {busy ? (<Spinner size="sm" style={{ width: '1.1rem', height: '1.1rem' }}> {''} </Spinner>) : null}
  </button>
);

export const OutlineButton = ({ children, onClick, disabled, style }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    style={{
      fontSize: 15, fontWeight: 600, border: `1px solid ${colors.blue}`, borderRadius: 999, padding: '10px 22px', background: '#FFFFFF', color: colors.blue,
      opacity: disabled ? 0.7 : 1, cursor: disabled ? 'default' : 'pointer', ...(style || {}),
    }}
  >
    {children}
  </button>
);

export const LinkButton = ({ children, onClick }) => (
  <button type="button" onClick={onClick} style={{ border: 0, background: 'transparent', padding: 0, color: colors.blue, fontSize: 15, fontWeight: 600, cursor: 'pointer', textDecoration: 'underline' }}>
    {children}
  </button>
);

// A numbered step: white card, blue number circle, content to the right
export const StepCard = ({ number, children }) => (
  <section style={{ ...cardStyle, display: 'flex', gap: 'clamp(12px, 2vw, 20px)' }}>
    {number ? (
      <span aria-hidden="true" style={{ flex: 'none', width: 40, height: 40, borderRadius: 999, background: colors.blue, color: '#FFFFFF', fontWeight: 600, fontSize: 18, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        {number}
      </span>
    ) : null}
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14, flex: 1, minWidth: 0 }}>
      {children}
    </div>
  </section>
);

// Step 1 layout: instructions on the left, the amber box on the right (stacks below on narrow screens)
export const StepWithAside = ({ children, aside }) => (
  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '20px 28px', alignItems: 'flex-start' }}>
    <div style={{ flex: '999 1 480px', minWidth: 0, display: 'flex', flexDirection: 'column', gap: 14 }}>
      {children}
    </div>
    {aside}
  </div>
);

export const KeepConnectedBox = ({ items }) => (
  <aside aria-label="Keeping Steppr connected" style={{ flex: '1 1 260px', minWidth: 0, display: 'flex', gap: 12, background: colors.amberBg, borderRadius: 12, padding: '16px 18px' }}>
    <svg aria-hidden="true" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={colors.amberIcon} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flex: 'none', marginTop: 2 }}>
      <path d="M12 3l9 16H3z" />
      <path d="M12 10v4" />
      <path d="M12 17h.01" />
    </svg>
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      <p style={{ margin: 0, fontSize: 15, fontWeight: 600, color: colors.amberText }}>Once connected, to keep Steppr connected, please don't</p>
      <ul style={{ margin: 0, paddingLeft: 20, fontSize: 15, lineHeight: 1.6, color: colors.amberText }}>
        {items.map((item) => (<li key={item}>{item}</li>))}
      </ul>
    </div>
  </aside>
);

export class CopyField extends Component {
  static propTypes = {
    label: PropTypes.string.isRequired,
    value: PropTypes.string.isRequired,
  };

  constructor(props) {
    super(props);
    this.state = { copied: false };
  }

  componentWillUnmount() {
    if (this.timer) clearTimeout(this.timer);
  }

  copy = () => {
    const { value } = this.props;

    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(value).then(() => this.showCopied()).catch(() => null);
      }
    } catch (err) {
      console.log('copy failed');
    }
  }

  showCopied = () => {
    this.setState({ copied: true });
    if (this.timer) clearTimeout(this.timer);
    this.timer = setTimeout(() => this.setState({ copied: false }), 2000);
  }

  render() {
    const { label, value } = this.props;
    const { copied } = this.state;

    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, background: colors.page, borderRadius: 10, padding: '8px 8px 8px 14px', minWidth: 0, maxWidth: '100%' }}>
        <span style={{ fontSize: 14, color: colors.muted, flex: 'none' }}>{label}</span>
        <code style={{ fontSize: 14, color: colors.text, background: 'transparent', padding: 0, overflowWrap: 'anywhere', minWidth: 0 }}>{value}</code>
        <button
          type="button"
          aria-label={`Copy ${label.toLowerCase()}`}
          onClick={this.copy}
          style={{ flex: 'none', fontSize: 13, fontWeight: 600, border: 0, borderRadius: 8, padding: '6px 12px', background: colors.blueLight, color: colors.blueDark, cursor: 'pointer' }}
        >
          {copied ? 'Copied' : 'Copy'}
        </button>
      </div>
    );
  }
}

// Three inputs side by side; they wrap one under another on narrow screens
export const FieldRow = ({ children }) => (
  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16 }}>
    {children}
  </div>
);

export const Field = ({ id, label, children }) => (
  <div style={{ flex: '1 1 220px', minWidth: 0, display: 'flex', flexDirection: 'column' }}>
    <label htmlFor={id} style={labelStyle}>{label}</label>
    {children}
  </div>
);

// lines: [{ ok: true|false, text }]; title optional
export const ResultList = ({ lines, title }) => {
  if (!lines || !lines.length) return null;

  return (
    <div role="status" style={{ display: 'flex', flexDirection: 'column', gap: 8, background: '#FFFFFF', border: `1px solid ${colors.borderLight}`, borderRadius: 12, padding: '16px 18px' }}>
      {title ? (<p style={{ margin: '0 0 4px', fontSize: 14, fontWeight: 600, color: colors.muted }}>{title}</p>) : null}
      {lines.map((line) => (
        <p key={line.key || line.text} style={{ margin: 0, fontSize: 15, display: 'flex', gap: 10, alignItems: 'flex-start', color: colors.text }}>
          {line.ok ? (<CheckIcon />) : (<CrossIcon />)}
          <span>{line.text}</span>
        </p>
      ))}
    </div>
  );
};

export const ConnectedPill = ({ children, problem }) => (problem ? (
  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, background: colors.amberBg, color: colors.amberText, fontWeight: 600, fontSize: 15, borderRadius: 999, padding: '6px 14px' }}>
    <svg aria-hidden="true" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ flex: 'none' }}>
      <path d="M12 9v4M12 17h.01M10.3 3.9L1.8 18a2 2 0 001.7 3h17a2 2 0 001.7-3L13.7 3.9a2 2 0 00-3.4 0z" />
    </svg>
    {children || 'Connection problem'}
  </span>
) : (
  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, background: colors.greenLight, color: colors.green, fontWeight: 600, fontSize: 15, borderRadius: 999, padding: '6px 14px' }}>
    <CheckIcon color="currentColor" size={16} />
    {children || 'Connected'}
  </span>
));

// What the latest status check says about a saved connection: { valid, error } from the business record (the same
// fields as the header's Website Connection light). valid === false means the last check failed.
export const connectionProblem = (status) => (status && status.valid === false
  ? (String(status.error || '').replace(/\{\{\w+\}\}/g, '').trim() || 'The last check could not reach your shop.')
  : null);

// The one-line summary under the steps once a shop is connected
export const ConnectedSummary = ({ pill, problem, problemHelp, address, secretText, checkedText, extra, buttonText, onButton, buttonDisabled }) => (
  <section style={{ ...cardStyle, display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '16px 28px' }}>
    <ConnectedPill problem={!!problem}>{pill}</ConnectedPill>
    {address ? (<span style={{ fontSize: 16, overflowWrap: 'anywhere' }}>{address}</span>) : null}
    {secretText ? (<span style={{ fontSize: 15, color: colors.muted }}>{secretText}</span>) : null}
    {checkedText ? (<span style={{ fontSize: 14, color: colors.muted }}>{checkedText}</span>) : null}
    {extra || null}
    {(buttonText && onButton) ? (
      <OutlineButton onClick={onButton} disabled={buttonDisabled} style={{ marginLeft: 'auto', fontSize: 16, padding: '10px 26px' }}>{buttonText}</OutlineButton>
    ) : null}
    {problem ? (
      <div style={{ flexBasis: '100%', fontSize: 15, color: colors.amberText }}>
        <strong>{problem}.</strong> {problemHelp || ''}
      </div>
    ) : null}
  </section>
);
